import {
  FeatureFlagProvider,
  FeatureFlagsController,
  FeatureFlagTestProvider,
  type FeatureFlagsMocks,
} from '@seek/candidate-feature-flag';
import type { ReactNode } from 'react';

import { experimentsConfig, featureFlagsConfig } from 'src/constants/flags';

/**
 * This wrapper exists purely to mock the values in Cypress.
 * In cypress, add experiment values like this: ?experiments=exp-key-1:exp-val-1,exp-key-2:exp-val-2
 *
 * For mocking for Jest tests, search for the testRender function.
 */

interface FeatureFlagWrapperProps {
  children: ReactNode;
  solId: string;
  debug?: boolean;
}

const isTestEnvironment = (): boolean => {
  // Detect Cypress environment
  if (typeof window !== 'undefined' && (window as any).Cypress !== undefined) {
    return true;
  }

  return false;
};

const parseExperimentMocks = (): FeatureFlagsMocks => {
  if (typeof window === 'undefined') {
    return {};
  }

  const urlParams = new URLSearchParams(window.location.search);
  const experiments = urlParams.get('experiments');

  if (!experiments) {
    return {};
  }

  const mocks: FeatureFlagsMocks = {};

  // Parse format: ?experiments=test-one:value-one,test-two:value-two
  experiments.split(',').forEach((experiment) => {
    const [key, value] = experiment.split(':');
    if (key && value) {
      mocks[key.trim()] = value.trim();
    }
  });

  return mocks;
};

export const FeatureFlagWrapper = ({
  children,
  solId,
  debug = false,
}: FeatureFlagWrapperProps) => {
  const isTest = isTestEnvironment();

  if (isTest) {
    const mocks = parseExperimentMocks();

    return (
      <FeatureFlagTestProvider mocks={mocks}>
        {children}
      </FeatureFlagTestProvider>
    );
  }

  return (
    <FeatureFlagProvider
      featureFlagsConfig={featureFlagsConfig}
      experimentsConfig={experimentsConfig}
      solId={solId}
      customContext={{
        applicationContext: {
          key: solId,
          namespace: 'ca-jobapply-frontend',
          build: '4.0.0',
          version: '4.0.0',
        },
      }}
      shouldEnableOverride
      debug={debug}
    >
      {children}
      <FeatureFlagsController />
    </FeatureFlagProvider>
  );
};
